import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import {
  AutomationConditionGroupOperationEnum,
  AutomationConditionGroupUpdateDocument,
  AutomationConditionGroupUpdateMutation,
  AutomationConditionGroupUpdateMutationVariables,
  AutomationFragment as AutomationType
} from "@typing/Generated";

type AutomationConditionGroupType = Extract<
  AutomationType["blocks"][number],
  { __typename: "AutomationConditionBlock" }
>["automationConditionGroups"][number];

type Props = {
  automationConditionGroup: AutomationConditionGroupType;
  locked: boolean;
};

type FormData = {
  operation: AutomationConditionGroupOperationEnum | null;
};

const AutomationConditionGroupForm = ({ automationConditionGroup, locked }: Props) => {
  const intl = useIntl();

  const defaultValues: FormData = {
    operation: automationConditionGroup.operation
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      operation: yup
        .string()
        .required(intl.formatMessage({ id: "errors.automationConditionGroup.operation.blank" }))
        .oneOf(
          Object.values(AutomationConditionGroupOperationEnum),
          intl.formatMessage({ id: "errors.automationConditionGroup.operation.inclusion" })
        )
    })
    .required();

  const [updateAutomationConditionGroup] = useMutation<
    AutomationConditionGroupUpdateMutation,
    AutomationConditionGroupUpdateMutationVariables
  >(AutomationConditionGroupUpdateDocument);

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const operation = form.watch("operation");

  useEffect(() => {
    updateAutomationConditionGroup({
      variables: {
        id: automationConditionGroup.id,
        operation
      }
    });
  }, [automationConditionGroup.id, operation, updateAutomationConditionGroup]);

  const operationOptions = useMemo(() => {
    const options = [AutomationConditionGroupOperationEnum.AND, AutomationConditionGroupOperationEnum.OR];
    return options.map(operationOption => ({
      label: intl.formatMessage({ id: `models.automationConditionGroup.operations.${operationOption}` }),
      value: operationOption
    }));
  }, [intl]);

  return (
    <Form hookForm={form} onSubmit={() => {}}>
      <div>
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.operation}
          label={intl.formatMessage({ id: "forms.automationConditionGroup.operationLabel" })}
          name="operation"
          options={operationOptions}
        />
      </div>
    </Form>
  );
};

export default AutomationConditionGroupForm;
